// Load REAL brand/curated Google Fonts for the editor canvas, mirroring the worker's
// font registry policy (apps/socialkit/src/core/visual/v2/font-registry.ts): the
// DISPLAY family loads the heaviest available weight of 900..400 (the master
// registers that face AS weight 400, and CSS closest-match does the same here when
// only one weight exists under the family), the BODY family loads 400 + 600, and a
// true-italic run additionally loads that family's ital axis. Vendored/logical
// faces (Display/Body/Serif + RTL) stay on the bundled @font-face set (fonts.css).
//
// Requests go through the lenient v1 CSS API (fonts.googleapis.com/css): one request
// carries the whole weight ladder and Google silently drops the weights a family
// lacks. The css2 API instead 400s on any missing weight WITHOUT CORS headers, so
// probing the ladder there spewed CORS console errors on every composer mount.

import { useEffect } from 'react'
import type { PostComposition } from './types'

/** The bundled logical faces — never fetched remotely. Shared with the editor's font
 *  menu so "is this a real brand family" has ONE answer. */
export const LOGICAL_FONT_FAMILIES = new Set([
  'Display',
  'Body',
  'Serif',
  'DisplayHe',
  'BodyHe',
  'DisplayAr',
  'BodyAr',
])
// Mirror of the registry's FAMILY_RE (no quotes/specials — these names reach CSS).
const FAMILY_RE = /^[A-Za-z0-9][A-Za-z0-9 -]{0,49}$/
const DISPLAY_LADDER = [900, 800, 700, 600, 400] as const

// In-flight/settled loads, app-global (document-level @font-face). A load that FULLY
// fails deletes its entry so the next canvas mount retries — a transient network blip
// at mount must not pin the fallback face for the whole session.
const loads = new Map<string, Promise<boolean>>()

async function cssV1(family: string, variants: string): Promise<string | null> {
  const fam = encodeURIComponent(family).replace(/%20/g, '+')
  try {
    const res = await fetch(
      `https://fonts.googleapis.com/css?family=${fam}:${variants}&display=swap`
    )
    if (!res.ok) return null
    return await res.text()
  } catch {
    // Offline / blocked network: the canvas falls back to the bundled faces (expected).
    return null
  }
}

function inject(css: string, key: string): void {
  const el = document.createElement('style')
  el.dataset.compositionFont = key
  el.textContent = css
  document.head.appendChild(el)
}

/** Keep only the @font-face blocks carrying the heaviest font-weight in the sheet, so a
 *  multi-weight family still registers ONE face and CSS closest-match maps every weight
 *  to it, exactly like the worker registers the heaviest face as 400. */
function keepHeaviest(css: string): string | null {
  const blocks = css.match(/@font-face\s*{[^}]*}/g)
  if (!blocks || blocks.length === 0) return null
  const weightOf = (b: string) => Number(b.match(/font-weight:\s*(\d+)/)?.[1] ?? 0)
  const max = Math.max(...blocks.map(weightOf))
  return blocks.filter(b => weightOf(b) === max).join('\n')
}

/** One lenient request for the whole ladder; inject only the heaviest face returned. */
async function injectLadder(family: string, key: string, italic: boolean): Promise<boolean> {
  const variants = DISPLAY_LADDER.map(w => (italic ? `${w}i` : `${w}`)).join(',')
  const css = await cssV1(family, variants)
  const hit = css && keepHeaviest(css)
  if (!hit) return false
  inject(hit, key)
  return true
}

function ensure(key: string, run: () => Promise<boolean>): Promise<boolean> {
  const existing = loads.get(key)
  if (existing) return existing
  const p = run().then(
    ok => {
      if (!ok) loads.delete(key)
      return ok
    },
    () => {
      loads.delete(key)
      return false
    }
  )
  loads.set(key, p)
  return p
}

function ensureFamily(family: string, role: 'display' | 'body', italic: boolean): void {
  ensure(`${family}:${role}`, async () => {
    if (role === 'body') {
      const css = await cssV1(family, '400,600')
      if (css) inject(css, `${family}:body`)
      return !!css
    }
    return injectLadder(family, `${family}:display`, false)
  })
  if (italic) ensure(`${family}:ital`, () => injectLadder(family, `${family}:ital`, true))
}

/** Eagerly load a family's display ladder (menu previews / a user's manual pick).
 *  Resolves true once a real face is registered — the FontPicker uses the result to
 *  accept or reject a typed family. */
export function probeFamily(family: string): Promise<boolean> {
  const fam = family.trim()
  if (!fam || LOGICAL_FONT_FAMILIES.has(fam) || !FAMILY_RE.test(fam)) return Promise.resolve(false)
  return ensure(`${fam}:display`, () => injectLadder(fam, `${fam}:display`, false))
}

type FontNeed = { family: string; role: 'display' | 'body'; italic: boolean }

export function collectFontNeeds(comp: PostComposition): FontNeed[] {
  const needs = new Map<string, FontNeed>()
  const displayFam = comp.brand?.fonts?.display
  const bodyFam = comp.brand?.fonts?.body
  for (const l of comp.layers) {
    if (l.type !== 'text') continue
    const uses: Array<{ family: string | undefined; italic: boolean }> = [
      { family: l.fontFamily, italic: false },
      ...l.runs.map(r => ({ family: r.fontFamily ?? l.fontFamily, italic: !!r.italic })),
    ]
    for (const u of uses) {
      const fam = u.family
      if (!fam || LOGICAL_FONT_FAMILIES.has(fam) || !FAMILY_RE.test(fam)) continue
      // A family serving BOTH roles (single-family kits) takes the display ladder.
      const role: FontNeed['role'] = fam === bodyFam && fam !== displayFam ? 'body' : 'display'
      const cur = needs.get(fam)
      if (cur) cur.italic = cur.italic || u.italic
      else needs.set(fam, { family: fam, role, italic: u.italic })
    }
  }
  return [...needs.values()]
}

/** Ensure every REAL font family this composition references is loaded in the browser. */
export function useCompositionGoogleFonts(comp: PostComposition | null | undefined): void {
  useEffect(() => {
    if (!comp) return
    for (const n of collectFontNeeds(comp)) ensureFamily(n.family, n.role, n.italic)
  }, [comp])
}
